import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import AppRoutes from 'constants/AppRoutes';
import { DATA_OFFSET, DATA_SET_PAGINATION_COUNT, SEARCH_PARAMS } from 'constants/dataSets';
import { SEARCH, SORT, STATUS } from 'constants/formFields';
import {
  DEFAULT_ORDER_BY, KEY_PERMISSIONS, MATERIALITY_ASSESSMENT_STATUS, TASK_MANAGEMENT_PERMISSIONS, USER_ROLES,
} from 'constants/general';
import usePillarModulePermission from 'hooks/usePillarModulePermission';
import { setStakeholderVcTab } from 'store/slices/general/slice';
import {
  useApproveMAMutation, useDeleteMaterialityAssessmentMutation, useGetMaterialityAssessmentsQuery, useRejectMAMutation,
} from 'store/slices/materialAssessment/apis/materialAssessmentApi';
import { useUserMeQuery } from 'store/slices/user/apis/user';

import useDataMaterialityListActions from './hooks/useDataMaterialityAssesmentActions';
import useMaterialityAssessmentListForm from './hooks/useMaterialityAssessmentListForm';
import MaterialityAssessmentView from './MaterialityAssessmentView';

import type IDetailsError from 'store/slices/materialAssessment/interfaces/IDetailsError';
import type IMaterialAssessmentItem from 'store/slices/materialAssessment/interfaces/IMaterialAssessmentItem';

function MaterialityAssessment() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [orderBy, setOrderBy] = useState(DEFAULT_ORDER_BY);
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const searchParam = searchParams.get(SEARCH_PARAMS.search) || '';
  const offset = Number(searchParams.get(SEARCH_PARAMS.offset) || 0);
  const [listMA, setListMA] = useState<IMaterialAssessmentItem[]>([]);
  const [openDeleteModal, setDeleteModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<number | null>(null);
  const [openErrorDetailsModal, setOpenErrorDetailsModal] = useState(false);
  const [errorDetails, setErrorDetails] = useState<IDetailsError | undefined>();
  const { data: userData, isLoading: isUserLoading } = useUserMeQuery();

  const [approveMA] = useApproveMAMutation();
  const [rejectMA] = useRejectMAMutation();

  const showAction = userData?.data?.role === USER_ROLES.COMPANY_ADMIN
  || userData?.data?.role === USER_ROLES.SIGNATORY;

  const {
    control, getValues, setValue, watch,
  } = useMaterialityAssessmentListForm();

  const {
    access,
    isDisabled,
  } = usePillarModulePermission({
    pillarModuleKey: KEY_PERMISSIONS.MATERIALITY_ASSESSMENT_MANAGEMENT,
    userId: userData?.data?.id,
  });
  const isViewOnlyOrSignOff = access === TASK_MANAGEMENT_PERMISSIONS.VIEW_ONLY
    || access === TASK_MANAGEMENT_PERMISSIONS.SIGN_OFF;
  const isDisabledAccess = isDisabled || access === TASK_MANAGEMENT_PERMISSIONS.VIEW_ONLY;
  const isCreateMaterialityAssessmentDisabled = isDisabledAccess || isViewOnlyOrSignOff;

  const {
    onSearchChange,
    onSortChange,
  } = useDataMaterialityListActions({
    setOrderBy,
    getValues,
    setValue,
  });

  const filteredStatuses = useMemo(
    () => (watch(STATUS) || [])?.filter((item): item is string => item !== undefined),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(watch(STATUS))],
  );

  const { data, isFetching: isMaterialityListLoading } = useGetMaterialityAssessmentsQuery({
    orderBy,
    keyword: searchParam,
    offset,
    statuses: filteredStatuses,
  });
  const { meta, items } = data?.data || {};

  const [deleteMaterialityAssessment] = useDeleteMaterialityAssessmentMutation();

  useEffect(() => {
    setSearchParams((prev) => ({
      ...prev,
      [SEARCH_PARAMS.offset]: (DATA_OFFSET).toString(),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onResetOffset = () => {
    setSearchParams((prev) => ({
      ...prev,
      [SEARCH_PARAMS.offset]: (DATA_OFFSET).toString(),
    }));
    setListMA([]);
  };

  useEffect(() => {
    if (offset === 0) {
      setListMA([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam, JSON.stringify(filteredStatuses), offset]);

  useEffect(() => {
    if (items !== undefined) {
      if (items.length === 0 && offset === 0) {
        setListMA([]);
        return;
      }

      if (items.length > 0) {
        setListMA((prev) => {
          const prevIds = new Set(prev.map((item) => item.id));
          const newItems = items.filter((item) => !prevIds.has(item.id));

          if (offset === 0) {
            return items;
          }

          return [...prev, ...newItems];
        });
      }
    }
  }, [items, offset]);

  const loading = isMaterialityListLoading;
  const filterOff = watch(STATUS)?.length === 0
    && watch(SEARCH)?.length === 0
    && watch(SORT)?.orderKey === ''
    && (location.search === '' || location.search === '?offset=0');

  const isEmptyList = (!data?.data?.meta?.count || data?.data?.items?.length === 0)
    && filterOff && !isMaterialityListLoading;

  const goToInitiateMaterialityAssessment = () => {
    if (isCreateMaterialityAssessmentDisabled) {
      return;
    }

    navigate(AppRoutes.materialityAssessmentInitiate.replace(':recordId', ''));
  };

  const onGetMoreItems = () => {
    setSearchParams((prev) => ({
      ...prev,
      [SEARCH_PARAMS.offset]: (offset + DATA_SET_PAGINATION_COUNT).toString(),
    }));
  };

  const onEdit = (id: number) => {
    navigate(AppRoutes.materialityAssessmentInitiate.replace(':recordId', id.toString()));
  };

  const onConfirm = () => {
    if (selectedItem) {
      setSearchParams((prev) => ({
        ...prev,
        [SEARCH_PARAMS.offset]: (DATA_OFFSET).toString(),
      }));

      deleteMaterialityAssessment({ id: selectedItem }).unwrap().then(() => {
        setListMA((prev) => prev.filter((item) => item.id !== selectedItem));
      }).catch((error) => {
        setOpenErrorDetailsModal(true);
        setErrorDetails(error.data.message[0][0]);
      });
    }
    setDeleteModal(false);
  };

  const onCancel = () => {
    setDeleteModal(false);
  };

  const onDelete = (id: number) => {
    setDeleteModal(true);
    setSelectedItem(id);
  };

  const handleCloseErrorDetailsModal = () => {
    setOpenErrorDetailsModal(false);
    setErrorDetails(undefined);
  };

  const handleGoLink = (id: number) => {
    navigate(AppRoutes.taskManagementId.replace(':taskId', id.toString()));
  };

  useEffect(() => {
    dispatch(setStakeholderVcTab(null));
  }, [dispatch]);

  const handleApprove = (id: number) => {
    approveMA({ id }).unwrap().then(() => {
      setListMA((prev) => prev.map((item) => (
        item.id === id
          ? { ...item, status: MATERIALITY_ASSESSMENT_STATUS.APPROVED }
          : item
      )));
      toast.success(t('notifications.materialAssessmentApproved'));
    });
  };

  const handleReject = (id: number) => {
    rejectMA({ id }).unwrap().then(() => {
      toast.success(t('notifications.materialAssessmentRejected'));
    });
  };

  return (
    <MaterialityAssessmentView
      isEmptyList={isEmptyList}
      loading={loading || isUserLoading}
      goToInitiateMaterialityAssessment={goToInitiateMaterialityAssessment}
      control={control}
      onSearchChange={onSearchChange}
      onSortChange={onSortChange}
      onGetMoreItems={onGetMoreItems}
      itemsList={listMA || []}
      hasNext={!!meta?.hasNext}
      onEdit={onEdit}
      onDelete={onDelete}
      isDeleteModalOpen={openDeleteModal}
      onCloseDeleteModal={onCancel}
      onDeleteSwimlaneConfirm={onConfirm}
      onResetOffset={onResetOffset}
      openErrorDetailsModal={openErrorDetailsModal}
      errorDetails={errorDetails}
      handleCloseErrorDetailsModal={handleCloseErrorDetailsModal}
      handleGoLink={handleGoLink}
      handleApprove={handleApprove}
      handleReject={handleReject}
      showAction={showAction}
      isDisabledAccess={isDisabledAccess}
      isCreateMaterialityAssessmentDisabled={isCreateMaterialityAssessmentDisabled}
      isSignOff={access === TASK_MANAGEMENT_PERMISSIONS.SIGN_OFF}
      isViewOnlyOrSignOff={isViewOnlyOrSignOff}
    />
  );
}

export default MaterialityAssessment;
